import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { ViewToken } from '@shopify/flash-list';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  TouchableOpacity,
  FlatList,
  Dimensions,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import uuid from 'react-native-uuid';

import Avatar from '@components/Avatar/Avatar';
import { ChatAssignToTaskModal } from '@components/Chat/ChatAssignToTaskModal.web';
import ChatForm from '@components/Chat/ChatForm';
import ChatMessageList, {
  ChatMessageListItem,
} from '@components/Chat/ChatMessageList.web';
import { ChatOnboardingNudge } from '@components/Chat/ChatOnboardingNudge.web';
import { ChatPhotoRequestNudge } from '@components/Chat/ChatPhotoRequestNudge.web';
import { ChatSearchText } from '@components/Chat/ChatSearchText';
import { EditMyTagsModal } from '@components/Chat/EditMyTagsModal.web';
import { MyTagModal } from '@components/Chat/MyTagModal.web';
import NewMessageIndicator from '@components/Chat/NewMessageIndicator';
import { DropZone } from '@components/DropZone';
import { ChatMessageReadByModal } from '@components/Modals/ChatMessageReadByModal';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { NetInfoModal } from '@components/Modals/NetInfoModal.web';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import AcceptDeclineButtons from '@components/shared/Button/AcceptDeclineButtons';
import Icon from '@components/shared/Icon/Icon';
import ChatOverlay from '@components/Tutorialization/Web/ChatOverlay';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  Message,
  Task,
  Project,
  MessageTask,
  MessageProject,
  Document,
  Chat,
  useSearchLazyQuery,
  useGetChatQuery,
  useListPendingUserRequestsQuery,
  useMarkChatsReadMutation,
  useUpdateOnboardingStateMutation,
  UserRequestCategory,
  UserRequestable,
  GetMeDocument,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useActiveTooltip } from '@hooks/useActiveTooltip';
import useChatAvatar from '@hooks/useChatAvatar';
import useChatInput from '@hooks/useChatInput';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import { useDeleteMessage } from '@hooks/useDeleteMessage';
import useEmoji from '@hooks/useEmoji';
import { useIsInvitedNewUser } from '@hooks/useInvitedUserOnboarding';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import useSearch from '@hooks/useSearch';
import useSendMessage from '@hooks/useSendMessage';
import { ForwardMessage } from '@screens/Chats/ForwardMessage.web';
import { useNetInfo } from '@src/context/NetInfoContext';
import theme from '@themes/theme';
import { requestChatMarkedRead } from '@utils/chat/chatReadGuard';
import { mapLocalFileToDraftDocument } from '@utils/fileUtils';
import { INVITE_SIGNUP_START_CONVERSATION_KEY } from '@utils/startConversationTutorial';
import { AsyncStorage } from '@utils/storage';

import type { OlderPageStatus } from '@screens/Chats/ChatMessageData';

export const shouldTriggerUploadOnboarding = (
  firstTagUsed: boolean,
  hasFired: boolean
): boolean => !firstTagUsed && !hasFired;

// TA-11079: exiting search preview pages the LIVE list toward the previewed
// message while the preview stays on screen. At most this many pages of
// EXIT_WALK_PAGE_SIZE (300 messages) and EXIT_WALK_TIMEOUT_MS overall; past
// either, it falls back to switching at once and the existing re-jump.
export const EXIT_WALK_MAX_PAGES = 3;
export const EXIT_WALK_PAGE_SIZE = 100;
export const EXIT_WALK_TIMEOUT_MS = 8000;
// ... and how long the fallback re-jump may keep paging toward the message.
export const EXIT_FALLBACK_TIMEOUT_MS = 8000;

// TA-11067: how many times (1s apart) scrollToMessageId waits for its target
// to reach the rendered list before giving up — long enough for a chat search
// preview's listNearbyMessages window to load and render.
export const SCROLL_TO_MESSAGE_RETRIES = 10;

const ChatDetail: React.FC<{
  messages: Message[];
  fetchAfterCursor: () => void | Promise<OlderPageStatus>;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  chatId: Chat['id'];
  messageIdNeedScrollTo?: string;
  hasNextPage?: boolean;
  isPaginationResolved?: boolean;
  noNeedRefetchRef?: React.RefObject<boolean>;
  fetchedNewerMsgId?: string | null;
  onFetchedNewerMsgIdConsumed?: () => void;
  isInLiveList?: (id: string) => boolean;
  fetchLiveOlderPage?: (pageSize?: number) => Promise<OlderPageStatus>;
  liveLoading?: boolean;
}> = ({
  messages,
  fetchAfterCursor,
  loading,
  refreshing,
  onRefresh,
  chatId,
  messageIdNeedScrollTo = '',
  hasNextPage = true,
  isPaginationResolved = true,
  noNeedRefetchRef,
  fetchedNewerMsgId,
  onFetchedNewerMsgIdConsumed,
  isInLiveList,
  fetchLiveOlderPage,
  liveLoading = false,
}) => {
  const insets = useSafeAreaInsets();
  const { getChatAvatarLabel } = useChatAvatar();
  const navigation = useNavigation();
  const {
    isVisibleForChat,
    showOverlayForChat,
    chatHeaderHeight,
    isFromChatList,
    chatTutorialWebState,
  } = useChatTutorialWeb();

  const [chatFormHeight, setChatFormHeight] = useState<number>(0);
  const [chatMessageListHeight, setChatMessageListHeight] = useState<number>(0);

  const maxHeight =
    Dimensions.get('window').height -
    chatFormHeight -
    (chatHeaderHeight > 73 ? chatHeaderHeight : 73);

  const {
    search,
    setSearch,
    filter,
    isSearching,
    setIsSearching,
    isSearchPreview,
    setIsSearchPreview,
    unavailableMessageIds,
  } = useSearch();
  const {
    activeChat,
    isEditTagModalOpen,
    setIsEditTagModalOpen,
    isForwardMessageModalOpen,
    setIsForwardMessageModalOpen,
    isDeleteMessageModalOpen,
    setIsDeleteMessageModalOpen,
    displayMessageList,
    setIsScrolling,
    scrollRequested,
    setScrollRequested,
    setActiveMessageId,
    isReadByModalOpen,
    setIsReadByModalOpen,
    setIsOpenChat,
    setActiveChat,
    isOpenChat,
  } = useActiveChat();

  const { setShouldChatListOpen, setChatListOpen, isTaskWebPanelOpen } =
    useWebDrawer();

  const { me } = useMe();
  // TA-10039: gate every TA-9668 web onboarding tooltip/nudge/coachmark in this
  // screen behind the server-computed onboardingComplete flag. Critical for
  // legacy users (onboardingComplete: true but blank onboarding sub-flags).
  const onboardingActive = useOnboardingActive();
  const { sendDraftMessage } = useSendMessage();
  const { t } = useTranslation();
  const _listRef = useRef<FlatList>(null);
  const { isConnected } = useNetInfo();
  const [isTagSelected, setTagSelected] = useState(false);
  const [isFilesSelected, setFilesSelected] = useState(false);
  const [isSearchMessageClicked, setIsSearchMessageClicked] = useState(false);
  const [firstClickRepliedMsg, setFirstClickRepliedMsg] = useState<Message>();
  const [needScrollToMessageId, setNeedScrollToMessageId] = useState('');
  const [repliedFromMessageId, setRepliedFromMessageId] = useState('');
  const [isNetInfoModalVisible, setIsNetInfoModalVisible] = useState(false);
  const { showMessageEmojiModal, setShowMessageEmojiModal } = useEmoji();

  const inviteMemberComplete =
    me?.onboardingState?.inviteMemberComplete ?? false;
  const firstChatSentState = me?.onboardingState?.firstChatSent ?? false;
  const firstTagUsedState = me?.onboardingState?.firstTagUsed ?? false;
  const [showOnboardingChatFromViewTask, setShowOnboardingChatFromViewTask] =
    useState(false);
  // TA-10920: the inviteMemberComplete branch is Maria's (owner) "chat for the
  // first time after member joined" step. Invited members only carry
  // inviteMemberComplete=true as a skip marker, so they are excluded from it;
  // the view-task branch is Carlos's (member) "understanding the task" step
  // and stays available to them.
  const isInvitedAccount = useIsInvitedNewUser();
  const showOnboardingChat =
    onboardingActive &&
    ((isInvitedAccount === false &&
      inviteMemberComplete &&
      !firstChatSentState) ||
      showOnboardingChatFromViewTask);
  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  const [firstMsgJustSent, setFirstMsgJustSent] = useState(false);
  const [showViewTaskSuccessMsg, setShowViewTaskSuccessMsg] = useState(false);
  const [firstTagJustUsed, setFirstTagJustUsed] = useState(false);
  const [showNudgeCard, setShowNudgeCard] = useState(false);
  const [assignTooltipDismissed, setAssignTooltipDismissed] = useState(false);
  const [assignTooltipChecked, setAssignTooltipChecked] = useState(false);
  const [showInviteSignupTooltip, setShowInviteSignupTooltip] = useState(false);
  const wasTaskWebPanelOpenRef = useRef(isTaskWebPanelOpen);
  const [showHashTagStep3Tooltip, setShowHashTagStep3Tooltip] = useState(false);
  const [showUploadTagTooltip, setShowUploadTagTooltip] = useState(false);
  const [isInUploadOnboardingFlow, setIsInUploadOnboardingFlow] =
    useState(false);
  const [firstTagProjectName, setFirstTagProjectName] = useState('');
  const tooltipOpacity = useRef(new Animated.Value(0)).current;
  const step3TooltipOpacity = useRef(new Animated.Value(0)).current;
  const firstMsgLabelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewTaskSuccessMsgTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const viewTaskPanelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const firstTagLabelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const onboardingMutationFiredRef = useRef(false);
  const firstTagMutationFiredRef = useRef(false);
  const uploadOnboardingFiredRef = useRef(false);

  const [showPhotoRequestNudge, setShowPhotoRequestNudge] = useState(false);

  const { isActive, show, hide } = useActiveTooltip('start-a-conversation', 1);

  const handleCoachmarkSeen = useCallback(() => {
    updateOnboardingState({
      variables: { attributes: { tagPillCoachmarkShown: true } },
      refetchQueries: [{ query: GetMeDocument }],
    });
  }, [updateOnboardingState]);

  useEffect(() => {
    if (!me?.id) return;
    let mounted = true;

    (async () => {
      try {
        const val = await AsyncStorage.getItem(`assignTooltipSeen_${me.id}`);
        if (mounted && val === 'true') setAssignTooltipDismissed(true);
      } catch {
        // ignore storage read errors
      } finally {
        if (mounted) setAssignTooltipChecked(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [me?.id]);

  useEffect(() => {
    return () => {
      if (firstMsgLabelTimerRef.current)
        clearTimeout(firstMsgLabelTimerRef.current);
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
      if (firstTagLabelTimerRef.current)
        clearTimeout(firstTagLabelTimerRef.current);
      if (viewTaskPanelTimerRef.current)
        clearTimeout(viewTaskPanelTimerRef.current);
      if (viewTaskSuccessMsgTimerRef.current)
        clearTimeout(viewTaskSuccessMsgTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isTaskWebPanelOpen && me?.onboardingState?.firstChatSent === false) {
      viewTaskPanelTimerRef.current = setTimeout(() => {
        onboardingMutationFiredRef.current = true;
        setShowOnboardingChatFromViewTask(true);
        updateOnboardingState({
          variables: { attributes: { firstChatSent: true } },
          refetchQueries: [{ query: GetMeDocument }],
        });
        setTimeout(() => setFocus(true), 50);
      }, 2000);
    } else {
      if (viewTaskPanelTimerRef.current)
        clearTimeout(viewTaskPanelTimerRef.current);
    }
  }, [isTaskWebPanelOpen]);

  const [markChatsRead] = useMarkChatsReadMutation();

  const derivedChatId = activeChat?.id || chatId;
  const { data: chatData } = useGetChatQuery({
    fetchPolicy: 'cache-first',
    variables: { id: derivedChatId },
    skip: !derivedChatId,
  });
  const { getChat: chat } = chatData || {};
  const requestAttributes = {
    requestType: UserRequestCategory.MemberAddition,
    userRequestableId: chat?.id ?? '',
    userRequestableType: UserRequestable.Chat,
  };
  const { data: pendingRequestData } = useListPendingUserRequestsQuery({
    variables: {
      attributes: requestAttributes,
    },
    skip: !chat?.id,
    fetchPolicy: 'cache-and-network',
  });
  const { isGroupChat, leaveOn, users, friendRequestAccepted } = chat || {};

  const invitedMember = chat?.users?.find((u) => u.id !== me?.id);
  // TA-10081: the "Get a site photo" nudge is assigner-facing copy ("Ask your
  // crew to send one"). Invited members (assignees) also get
  // inviteMemberComplete set on invite-signup, so showOnboardingChat is true
  // for them too — gate the nudge to the chat owner/assigner only, mirroring
  // the native isAssigner gate in MyChatDetail.
  const isChatOwner = !!me?.id && chat?.owner?.id === me?.id;
  const isHelpdeskChat =
    (!!process.env.SUPPORT_USER_ID &&
      invitedMember?.id === process.env.SUPPORT_USER_ID) ||
    invitedMember?.name === 'TaskTag Support';
  // Invite-link signups skip the owner onboarding gates: the first time they
  // open Task Details while this chat is already open, show the tooltip once
  // (#27). Read the flag on the closed→open transition. It is only cleared
  // once the tooltip actually shows, so a Task Details open with the chat
  // drawer collapsed leaves the chance for the next open.
  useEffect(() => {
    const opened = isTaskWebPanelOpen && !wasTaskWebPanelOpenRef.current;
    wasTaskWebPanelOpenRef.current = isTaskWebPanelOpen;
    // The chat must already be open when Task Details opens: ChatDetail stays
    // mounted while the drawer is collapsed, so mounting alone is not enough.
    if (
      opened &&
      isOpenChat &&
      globalThis.localStorage?.getItem?.(
        INVITE_SIGNUP_START_CONVERSATION_KEY
      ) === '1'
    ) {
      setShowInviteSignupTooltip(true);
    }
  }, [isTaskWebPanelOpen, isOpenChat]);

  // Opening a chat later during the same Task Details session doesn't count;
  // the flag stays set for the next Task Details open.
  useEffect(() => {
    if (!isOpenChat) setShowInviteSignupTooltip(false);
  }, [isOpenChat]);
  // Closing Chat Detail only collapses the drawer (ChatDetail stays mounted),
  // so gate on the panel being open or the portal tooltip lingers (#35).
  const showAssignTooltip =
    isOpenChat &&
    assignTooltipChecked &&
    (showOnboardingChat || showInviteSignupTooltip) &&
    !assignTooltipDismissed &&
    !isHelpdeskChat;

  // The delayed show must be cancelled when the panel closes (or the gate
  // flips), otherwise it fires after unmount and re-claims the tooltip slot
  // with nothing left to point at (TA-10892 #35).
  useEffect(() => {
    if (!showAssignTooltip) {
      hide();
      return;
    }
    const timer = setTimeout(show, 2000);
    return () => {
      clearTimeout(timer);
      hide();
    };
  }, [showAssignTooltip, hide, show]);

  useEffect(() => {
    if (isActive) {
      if (me?.id) {
        AsyncStorage.setItem(`assignTooltipSeen_${me.id}`, 'true').catch(
          () => {}
        );
      }
      globalThis.localStorage?.removeItem?.(
        INVITE_SIGNUP_START_CONVERSATION_KEY
      );
      // Don't clear showInviteSignupTooltip here: for an invite-signup Member
      // it is the only thing keeping showAssignTooltip true, so clearing it
      // would hide the tooltip one render after it shows. Dismissal ends it
      // via assignTooltipDismissed; closing the chat clears it above.
      Animated.timing(tooltipOpacity, {
        toValue: 1,
        duration: 300,
        delay: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isActive]);

  useEffect(() => {
    if (showHashTagStep3Tooltip) {
      step3TooltipOpacity.setValue(0);
      Animated.timing(step3TooltipOpacity, {
        toValue: 1,
        duration: 300,
        delay: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [showHashTagStep3Tooltip]);

  const currentUserPendingInvite =
    pendingRequestData?.listPendingUserRequests.filter(
      (t) => t.user?.id === me?.id && t.canCurrentUserDecide === true
    ) ?? [];

  // chat?.isPendingRequest === true && chat?.canCurrentUserDecide === true;
  const activeChatForCurrentThread =
    activeChat?.id === derivedChatId ? activeChat : undefined;
  const isGroupChatResolved =
    isGroupChat ?? activeChatForCurrentThread?.isGroupChat ?? false;
  const leaveOnResolved = leaveOn ?? activeChatForCurrentThread?.leaveOn;
  const friendRequestAcceptedResolved =
    friendRequestAccepted ?? activeChatForCurrentThread?.friendRequestAccepted;
  const activeChatUserIds = activeChatForCurrentThread?.userIds;
  const hasAuthoritativeChatUsers = Array.isArray(users);
  const isCurrentUserRemovedFromGroup =
    !!isGroupChatResolved &&
    !!me?.id &&
    hasAuthoritativeChatUsers &&
    !users.some((user) => user?.id === me.id);
  const isCurrentUserRemovedFromGroupFallback =
    !!isGroupChatResolved &&
    !!me?.id &&
    Array.isArray(activeChatUserIds) &&
    activeChatUserIds.length > 0 &&
    !activeChatUserIds.includes(me.id);
  const isCurrentUserRemovedFromGroupResolved = hasAuthoritativeChatUsers
    ? isCurrentUserRemovedFromGroup
    : isCurrentUserRemovedFromGroupFallback;
  const isGroupChatAccessRevoked =
    !!isGroupChatResolved && friendRequestAcceptedResolved === false;
  const shouldShowPendingInviteCard =
    chat?.isGroupChat === true &&
    chat?.owner?.id !== me?.id &&
    !chat?.users?.some((u) => u.id === me?.id) &&
    chat?.isPendingRequest === true &&
    currentUserPendingInvite.length > 0;
  const shouldShowNoLongerMessage =
    !!chat &&
    !shouldShowPendingInviteCard &&
    (Boolean(leaveOnResolved) ||
      isCurrentUserRemovedFromGroupResolved ||
      isGroupChatAccessRevoked);
  const shouldRenderChatForm =
    !shouldShowPendingInviteCard &&
    (Boolean(isGroupChatResolved) ||
      Boolean(friendRequestAcceptedResolved) ||
      shouldShowNoLongerMessage);
  const shouldRenderWebNoLongerMessageBar =
    Platform.OS === 'web' && shouldShowNoLongerMessage;
  const shouldRenderInputContainer =
    shouldRenderChatForm && !shouldRenderWebNoLongerMessageBar;

  const {
    message,
    selectedMessage,
    isTagModalOpen,
    getTagsCollection,
    getLocalFiles,
    getReplyMessage,
    setIsTagModalOpen,
    setMessage,
    isConfirmCompletingTask,
    setIsCompletingTask,
    subscribeToChatUpdates,
    dropFileHandler,
    isCompletingTask,
    isAssigningTask,
    setIsAssigningTask,
    isAssigningTaskId,
    isAssigningTaskStep2,
    focus,
    setFocus,
  } = useChatInput();

  const tagsCollection = getTagsCollection(chatId);
  const localFiles = getLocalFiles(chatId);
  const replyMessage = getReplyMessage(chatId);

  // Show upload onboarding tooltip only after a file/image actually appears
  // in the input (not on icon click), to avoid showing before the picker resolves.

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (localFiles.length === 0) return;
    handleUploadFirstTime();
  }, [localFiles.length]);

  const showHashTagStep2Tooltip =
    onboardingActive &&
    Platform.OS === 'web' &&
    (((firstChatSentState || onboardingMutationFiredRef.current) &&
      !firstTagUsedState) ||
      isInUploadOnboardingFlow) &&
    isTagModalOpen;

  const handleHighlightTagPress = () => {
    setShowNudgeCard(false);
    setIsCompletingTask(false);
    setIsTagModalOpen(true);
  };

  const handleUploadFirstTime = useCallback(() => {
    if (!onboardingActive) return;
    if (
      !shouldTriggerUploadOnboarding(
        firstTagUsedState,
        uploadOnboardingFiredRef.current
      )
    )
      return;
    uploadOnboardingFiredRef.current = true;
    setShowUploadTagTooltip(true);
    updateOnboardingState({
      variables: { attributes: { firstTagUsed: true } },
      refetchQueries: [{ query: GetMeDocument }],
    });
  }, [onboardingActive, firstTagUsedState, updateOnboardingState]);

  const handleUploadTooltipTryIt = () => {
    setShowUploadTagTooltip(false);
    setIsInUploadOnboardingFlow(true);
    setIsCompletingTask(false);
    setIsTagModalOpen(true);
  };

  const handleChatFirstActivity = useCallback(() => {
    if (!assignTooltipDismissed) {
      Animated.timing(tooltipOpacity, {
        toValue: 0,
        duration: 3000,
        useNativeDriver: true,
      }).start(() => setAssignTooltipDismissed(true));
    }
  }, [assignTooltipDismissed, tooltipOpacity]);

  const handleHashTagStep3Activity = useCallback(() => {
    Animated.timing(step3TooltipOpacity, {
      toValue: 0,
      duration: 2000,
      useNativeDriver: true,
    }).start(() => setShowHashTagStep3Tooltip(false));
  }, [step3TooltipOpacity]);

  //close pop when user navigate to other chat window
  const closeTagModal = useCallback(() => {
    setIsReadByModalOpen(false);
    setIsForwardMessageModalOpen(false);
    setIsEditTagModalOpen(false);
    setIsTagModalOpen(false);
    if (!isConfirmCompletingTask && isCompletingTask) {
      setIsCompletingTask(false);
    }
    if (isAssigningTask) {
      setIsAssigningTask(false);
    }
  }, [isConfirmCompletingTask, isCompletingTask, isAssigningTask]);

  const tagRef = useOutsideClick(closeTagModal);

  const [filteredMessages, setFilteredMessages] = useState<Message[]>([
    ...messages,
  ]);

  const [globalSearch, { data, loading: loadingSearch }] = useSearchLazyQuery();
  const [canLoadMore, setCanLoadMore] = useState(false);
  const searchFrom = useRef(0);
  const [repliedClicked, setRepliedClicked] = useState('');

  const globalSearchCall = useCallback(
    async (term: string, isTag: boolean, isFile: boolean) => {
      try {
        const queryResult = await globalSearch({
          variables: {
            term,
            size: 20,
            from: searchFrom.current,
            chatId,
            includeMessages: true,
            includeDocuments: false,
            includeProjects: false,
            includeTasks: false,
            tags: isTag,
            files: isFile,
            media: false,
          },
        });
        const data1 = queryResult.data?.search;
        if (data1) {
          if (
            data1.length > 0 &&
            data1[0].total > searchFrom.current + data1.length
          ) {
            searchFrom.current = searchFrom.current + data1.length;
            setCanLoadMore(true);
          } else {
            setCanLoadMore(false);
          }
        }
      } catch {
        // ignore search errors
      }
    },
    [globalSearch, chatId]
  );

  // onSearch is read from a ref inside useDebouncedSearch (see hook source),
  // so this closure doesn't need to be memoized/ref-wrapped to stay fresh —
  // that workaround, previously needed to avoid a stale closure over the
  // hand-rolled debounce, is no longer necessary (TA-10169).
  const handleGlobalSearch = (term: string) => {
    searchFrom.current = 0;
    setCanLoadMore(false);
    globalSearchCall(term, isTagSelected, isFilesSelected);
  };

  const { isPending, isBelowThreshold, hasSearched, gatedTerm } =
    useDebouncedSearch(search, handleGlobalSearch);
  // The filter-toggle effect below reads gatedTerm but is deliberately keyed
  // only on [isFilesSelected, isTagSelected] (not gatedTerm itself — see that
  // effect's own comment), so gatedTerm can't be listed as a dependency
  // without also making the effect refire whenever a new search settles,
  // double-firing alongside handleGlobalSearch's own call. A ref updated
  // every render lets the effect read the CURRENT gatedTerm without being
  // keyed on it (TA-10169).
  const latestGatedTermRef = useRef(gatedTerm);
  latestGatedTermRef.current = gatedTerm;
  // Safe merged-loading form: gating the whole expression on
  // !isBelowThreshold first (rather than `loadingSearch || isPending || ...`)
  // stops a request still resolving right as the term drops below threshold
  // from leaking a stale spinner through in browse mode (TA-10169).
  const isSearchResultLoading =
    !isBelowThreshold && (loadingSearch || isPending || !hasSearched);

  // TA-10279: `filteredMessages` only gets replaced once the new term's
  // query response lands (see the `setFilteredMessages` effect below) —
  // while a FRESH term's fetch is in flight (searchFrom.current === 0, i.e.
  // not a load-more page), Apollo still holds the PREVIOUS term's `data`,
  // so without this the previous term's stale bubbles stay on screen under
  // the loading skeleton. Blanking here (rather than in the state effect
  // itself) keeps the pagination/cursor state machine untouched — same
  // "blank the data while in flight" pattern as
  // `GlobalSearchList.web.tsx`'s `data: loading ? [] : list.data`.
  const chatMessageListData =
    isSearching && isSearchResultLoading && searchFrom.current === 0
      ? []
      : filteredMessages;

  // TA-10279: same first-open hold `ChatMessageList.web.tsx` uses for its
  // own bubble skeleton — reused here so this screen's "no messages yet"
  // empty states can't flash in during the 150ms minimum-display hold
  // (previously gated on the raw `!loading` alone).
  const showThreadSkeleton = useMinimumVisible(
    !isSearching && loading && messages.length === 0
  );

  // Reset the load-more offset whenever the term drops back below
  // threshold — useDebouncedSearch itself only tracks the debounce window,
  // not this component's own pagination state (TA-10169).
  useEffect(() => {
    if (isBelowThreshold) {
      searchFrom.current = 0;
      setCanLoadMore(false);
    }
  }, [isBelowThreshold]);

  // Toggling the Tags/Files filter doesn't change the search term itself, so
  // useDebouncedSearch's own fire-effect (keyed on the term) won't refire —
  // re-run the already-settled gatedTerm directly against the new filter.
  // globalSearchCall is stable (useCallback), so including it here is safe
  // and satisfies exhaustive-deps without causing extra fires (TA-10169).
  useEffect(() => {
    if (latestGatedTermRef.current) {
      searchFrom.current = 0;
      setCanLoadMore(false);
      globalSearchCall(
        latestGatedTermRef.current,
        isTagSelected,
        isFilesSelected
      );
    }
  }, [isFilesSelected, isTagSelected, globalSearchCall]);

  useEffect(() => {
    focus && showMessageEmojiModal && setShowMessageEmojiModal(false);
  }, [focus]);

  const { id } = activeChat || {};
  const activeChatId = id;

  // TA-9295: both receipts go through the shared per-chat coalescer rather
  // than straight to the mutation. Two reasons: a cleanup receipt that
  // commits after someone else's message would otherwise normalize its own
  // unreadCount 0 over a badge the guard had just restored, and the guard
  // needs the open/close receipt's watermark to decide whether an event it
  // suppressed mid-flight is still unread.
  const markChatReadThroughGuard = (chatIdToRead: string) =>
    requestChatMarkedRead(chatIdToRead, () =>
      markChatsRead({
        variables: { attributes: { chatIds: [chatIdToRead] } },
      })
    );

  useEffect(() => {
    if (activeChatId) {
      markChatReadThroughGuard(activeChatId);
    }
    setShowNewMessageIndicator(false);
    setFirstNewMsgId('');
  }, [activeChatId]);

  const activeChatIdRef = useRef(activeChatId);
  activeChatIdRef.current = activeChatId;
  useEffect(() => {
    return () => {
      const chatIdOnClose = activeChatIdRef.current;
      if (chatIdOnClose) {
        markChatReadThroughGuard(chatIdOnClose);
      }
    };
  }, []);

  const sendMessage = (message1?: string) => {
    if (!chat || !me) return false;

    const projects = tagsCollection?.map((item) => item.project) || [];
    const projectIds = projects.map((item) => item.id);

    const tasks = tagsCollection?.flatMap((item) => item.tasks) || [];
    const taskIds = tasks.map((item) => item.id);

    // NOTE: draftMessageId has multiple purposes:
    // - Default identity in Apollo uses ID and __typename. Assigning a UUID as a draft message ID is sufficiently unique.
    // - Associated objects with a draft message also need an ID. Reusing the same UUID in combination with a different __typename ensures uniqueness.
    //    For example, a draft message may have associated projects, tasks, and/or documents.
    // - Since a draft message may have multiple associated objects of the same type, we append an index value to maintain uniqueness.
    const draftMessageId = uuid.v4().toString();

    const messageTasks = tasks.map(
      (item: Task, index): MessageTask => ({
        __typename: 'MessageTask',
        id: `${draftMessageId}-${index}`,
        authorId: me.id,
        author: me,
        taskId: item.id,
        task: item,
      })
    );

    const messageProjects = projects.map(
      (item: Project, index): MessageProject => ({
        __typename: 'MessageProject',
        id: `${draftMessageId}-${index}`,
        authorId: me.id,
        author: me,
        projectId: item.id,
        project: item,
      })
    );

    const documents = localFiles.map((item, index): Document => {
      const documentId = `${draftMessageId}-${index}`;
      const draftDocument = mapLocalFileToDraftDocument(
        item,
        draftMessageId,
        me
      );

      return {
        ...draftDocument,
        id: documentId,
        name: item.name || item.clientId,
        file: {
          ...draftDocument.file,
          id: documentId,
          cdnBaseUrl: '',
          url: item.url,
        },
      };
    });

    const draftMessage = {
      __typename: 'Message' as const,
      id: draftMessageId,
      chatId: derivedChatId,
      clientId: draftMessageId,
      body: (isVisibleForChat && message1 && message1 !== ''
        ? message1
        : message
      ).trimEnd(),
      tasks,
      taskIds,
      projects,
      projectIds,
      authorId: me.id,
      author: me,
      createdAt: new Date().toISOString(),
      isSender: true,
      replyId: replyMessage?.id || null,
      replyMessage: replyMessage ?? null,
      isDraft: true,
      publishedAt: false,
      publish: false,
      readAt: null,
      readReceipts: null,
      isRetry: !isConnected,
      chat: {
        ...chat,
      },
      attachments: [...messageTasks, ...messageProjects, ...documents],
    };
    if (documents.length > 0 && !isConnected) {
      setIsNetInfoModalVisible(true);
    } else {
      setIsNetInfoModalVisible(false);
      // fromOpenChat: this is the composer of the thread on screen, so the
      // send also clears that chat's unread badge (TA-9295).
      sendDraftMessage(draftMessage, { fromOpenChat: true });

      if (showOnboardingChatFromViewTask && !isGroupChatResolved) {
        setShowOnboardingChatFromViewTask(false);
        setShowViewTaskSuccessMsg(true);
        if (viewTaskSuccessMsgTimerRef.current)
          clearTimeout(viewTaskSuccessMsgTimerRef.current);
        viewTaskSuccessMsgTimerRef.current = setTimeout(() => {
          setShowViewTaskSuccessMsg(false);
        }, 3000);
      }
      if (showOnboardingChat && !onboardingMutationFiredRef.current) {
        onboardingMutationFiredRef.current = true;
        updateOnboardingState({
          variables: { attributes: { firstChatSent: true } },
          refetchQueries: [{ query: GetMeDocument }],
        });
        setFirstMsgJustSent(true);
        firstMsgLabelTimerRef.current = setTimeout(() => {
          setFirstMsgJustSent(false);
        }, 3000);
        // Only the assigner (chat owner) should be nudged to request a site
        // photo — never the assignee who is the one sending photos (TA-10081).
        if (isChatOwner) {
          nudgeTimerRef.current = setTimeout(() => {
            setShowPhotoRequestNudge(true);
            updateOnboardingState({
              variables: { attributes: { photoRequestNudgeShown: true } },
              refetchQueries: [{ query: GetMeDocument }],
            });
          }, 3000);
        }
      }
      if (
        Platform.OS === 'web' &&
        isInUploadOnboardingFlow &&
        (tasks.length > 0 || projects.length > 0)
      ) {
        const savedProjectName = projects[0]?.name ?? '';
        setFirstTagProjectName(savedProjectName);
        setIsInUploadOnboardingFlow(false);
        setFirstTagJustUsed(true);
        firstTagLabelTimerRef.current = setTimeout(() => {
          setFirstTagJustUsed(false);
          setFirstTagProjectName('');
        }, 3000);
        setShowNudgeCard(false);
        setShowPhotoRequestNudge(false);
      } else if (
        Platform.OS === 'web' &&
        (firstChatSentState || onboardingMutationFiredRef.current) &&
        !firstTagUsedState &&
        !firstTagMutationFiredRef.current &&
        (tasks.length > 0 || projects.length > 0)
      ) {
        firstTagMutationFiredRef.current = true;
        updateOnboardingState({
          variables: { attributes: { firstTagUsed: true } },
          refetchQueries: [{ query: GetMeDocument }],
        });
        setFirstTagJustUsed(true);
        firstTagLabelTimerRef.current = setTimeout(() => {
          setFirstTagJustUsed(false);
        }, 3000);
        setShowNudgeCard(false);
        setShowPhotoRequestNudge(false);
      }
      if (Platform.OS === 'web' && showHashTagStep3Tooltip) {
        setShowHashTagStep3Tooltip(false);
      }
      // scroll to bottom on send
      messages.length &&
        _listRef.current?.scrollToIndex({
          index: 0,
          animated: false,
        });
    }
  };

  const selectedOption = (type: string) => {
    switch (type) {
      case 'Tags':
        setTagSelected(true);
        setFilesSelected(false);
        break;

      case 'Files':
        setFilesSelected(true);
        setTagSelected(false);
        break;
      default:
        setFilesSelected(false);
        setTagSelected(false);
        return false;
    }
  };

  useEffect(() => {
    if (filter.length > 0) {
      selectedOption(filter.toString());
    } else {
      selectedOption('');
    }
    // isBelowThreshold-gated rather than raw `search` truthiness: Apollo
    // retains the previous response's `data` even once the query stops
    // re-firing below threshold — reading it unguarded would leak a stale
    // search's messages in as if live (TA-10169).
    if (!isBelowThreshold) {
      const filterData = (data?.search ?? [])
        .filter((item) => item.record.__typename === 'Message')
        .map((item) => item.record as Message);
      if (filterData) {
        if (searchFrom.current > 0) {
          setFilteredMessages((prev) => [
            ...prev,
            ...filterData.filter((m) => !prev.some((m1) => m1.id === m.id)),
          ]);
        } else {
          setFilteredMessages(filterData);
        }
      }
    } else {
      setFilteredMessages(messages);
    }
  }, [isBelowThreshold, filter, messages, data]);

  useFocusEffect(
    useCallback(() => {
      // Subscribe to chat
      const unsubscribe = subscribeToChatUpdates?.();

      // Unsubscribe from chat updates when the screen is blurred
      return () => {
        unsubscribe?.();
      };
    }, [])
  );

  const closeTagModalOnBack = (e) => {
    if (!isTagModalOpen) {
      return;
    } else {
      e.preventDefault();
      setIsTagModalOpen(false);
      setIsCompletingTask(false);
      setIsEditTagModalOpen(false);
      setIsAssigningTask(false);
    }
  };
  useEffect(() => {
    navigation.addListener('beforeRemove', closeTagModalOnBack);
    return () => {
      navigation.removeListener('beforeRemove', closeTagModalOnBack);
    };
  }, [navigation, isTagModalOpen, isCompletingTask]);

  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
  const [firstNewMsgId, setFirstNewMsgId] = useState('');

  useEffect(() => {
    if (!fetchedNewerMsgId || isSearching) return;
    // TA-10150: only consume the signal — do NOT force the New Message
    // indicator here. Messages fetched by the catch-up land in the message
    // list, and ChatMessageList's own newest-message effect already routes
    // them with scroll awareness: at the bottom the list scrolls and shows
    // the message immediately; scrolled up it raises the indicator via the
    // showNewMessageIndicator callback. Forcing the indicator here ignored
    // the scroll position AND set firstNewMsgId, which freezes
    // displayMessageList syncing — users at the bottom saw the button
    // instead of the message, and already-rendered messages appeared to
    // vanish behind the frozen snapshot.
    onFetchedNewerMsgIdConsumed?.();
    // isSearching is a dep so a signal that arrives mid-search is consumed
    // when the search closes instead of being stuck behind the early return.
  }, [fetchedNewerMsgId, isSearching]);

  const onViewableMsgsChanged = (items: ViewToken[]) => {
    if (
      items.some(
        (x) => filteredMessages.length && x.item.id == filteredMessages[0].id
      )
    ) {
      setShowNewMessageIndicator(false);
      setFirstNewMsgId('');
    }
  };

  const scrollToFirstNewMsg = () => {
    setShowNewMessageIndicator(false);
    let xindex = 0;
    filteredMessages.forEach((v, i) => {
      if (v.id == firstNewMsgId) {
        xindex = i;
        setFirstNewMsgId('');
      }
    });
    filteredMessages.length &&
      _listRef.current?.scrollToIndex({
        index: xindex,
        animated: false,
      });
  };

  // TA-11067: a jump holds isScrolling true, then releases it 10s after it
  // settles. One shared reset timer: a new jump cancels the previous jump's
  // pending release, so an earlier jump can't flip isScrolling to false
  // partway through a later one.
  const scrollingResetTimerRef =
    useRef<ReturnType<typeof setTimeout>>(undefined);
  const startJumpScrolling = () => {
    clearTimeout(scrollingResetTimerRef.current);
    setIsScrolling(true);
  };
  const scheduleScrollingReset = () => {
    clearTimeout(scrollingResetTimerRef.current);
    scrollingResetTimerRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 10000);
  };
  useEffect(() => () => clearTimeout(scrollingResetTimerRef.current), []);

  useEffect(() => {
    if (
      filteredMessages.length > 0 &&
      repliedClicked &&
      _listRef?.current !== null
    ) {
      const item = filteredMessages.filter(
        (filterMessage) =>
          filterMessage?.id ===
          (isSearchMessageClicked
            ? repliedClicked?.id
            : repliedClicked?.replyMessage?.id)
      );
      fetchAfterCursor();

      if (item[0]?.id) {
        setRepliedFromMessageId(item[0]?.id);

        if (firstClickRepliedMsg) {
          startJumpScrolling();
          scrollToMessageId(item[0]?.id);
        }

        setTimeout(() => {
          !firstClickRepliedMsg && setFirstClickRepliedMsg(item[0]);
          setRepliedClicked('');
          setIsSearchMessageClicked(false);
          scheduleScrollingReset();
        }, 2000);
      } else {
        startJumpScrolling();
        _listRef.current?.scrollToIndex({
          index: filteredMessages.length - 1,
          animated: true,
        });
      }
    }
  }, [
    _listRef?.current,
    repliedFromMessageId,
    repliedClicked,
    filteredMessages,
    displayMessageList?.length,
  ]);

  useEffect(() => {
    firstClickRepliedMsg && scrollToMessageId(firstClickRepliedMsg.id);
  }, [firstClickRepliedMsg]);

  // TA-11067: the jump-to-message helper shared by quoted-reply cards,
  // in-chat search and chat search results. Its retry used to close over the
  // calling render's displayMessageList and fall back to whichever message an
  // earlier jump had stored — state that is never cleared on web, where
  // ChatDetail stays mounted across chats. A later jump whose target wasn't
  // rendered yet (a search result in another chat) then retried the previous
  // jump's message against a stale list forever and never reached its own.
  // Resolve against the live list and retry the same id, bounded.
  const displayMessageListRef = useRef(displayMessageList);
  // Synced after commit rather than written during render. Layout effects
  // run before the passive effects that start a jump, so they see this list.
  useLayoutEffect(() => {
    displayMessageListRef.current = displayMessageList;
  });
  const scrollRetryTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(scrollRetryTimerRef.current), []);

  const scrollToMessageId = (
    id: string,
    retriesLeft = SCROLL_TO_MESSAGE_RETRIES
  ) => {
    clearTimeout(scrollRetryTimerRef.current);
    const index = (displayMessageListRef.current ?? []).findIndex(
      (item: ChatMessageListItem) => typeof item !== 'string' && item?.id === id
    );

    if (index >= 0) {
      _listRef.current?.scrollToIndex({
        index,
        animated: false,
        viewPosition: 0.5,
      });
    } else if (retriesLeft > 0) {
      scrollRetryTimerRef.current = setTimeout(
        () => scrollToMessageId(id, retriesLeft - 1),
        1000
      );
    }
  };

  //find reply(scrollToReplyMessage) to message code https://github.com/Lovett-Commercial/tasktag-mobile/pull/1985 , TA-3313

  const scrollToReplyMessage = (message: Message) => {
    setRepliedClicked(message);
    const item = displayMessageList?.filter(
      (filterMessage) =>
        filterMessage?.id === (search ? message.id : message.replyMessage?.id)
    );
    item && setRepliedFromMessageId(item[0]?.id);
    startJumpScrolling();
    if (item?.[0]?.id) {
      scrollToMessageId(item[0]?.id);
    }
    search && isSearching && setIsSearchMessageClicked(true);
    search && setSearch('');
    isSearching && setIsSearching(false);
  };
  const onDrop = (files: File[]) => dropFileHandler(chatId, files);

  const { deleteMessageById } = useDeleteMessage();
  const onCompleted = () => {
    setIsDeleteMessageModalOpen(false);
  };

  const onErrorDelete = () => {
    setIsDeleteMessageModalOpen(false);
  };

  useEffect(() => {
    if (scrollRequested && _listRef?.current !== null) {
      setScrollRequested(false);
      messageIdNeedScrollTo &&
        setTimeout(() => {
          // TA-11078: the result was found deleted while this waited.
          if (unavailableMessageIdsRef.current.includes(messageIdNeedScrollTo))
            return;
          startJumpScrolling();
          setNeedScrollToMessageId(messageIdNeedScrollTo);
        }, 1000);
    }
  }, [scrollRequested, _listRef?.current]);

  // TA-11078: a search result found deleted (ChatMessageData marks it and
  // leaves preview) must not stay a jump target: the effect below would page
  // toward a message that never arrives, with its spinner up for good.
  const unavailableMessageIdsRef = useRef(unavailableMessageIds);
  useLayoutEffect(() => {
    unavailableMessageIdsRef.current = unavailableMessageIds;
  });
  useEffect(() => {
    if (
      needScrollToMessageId &&
      unavailableMessageIds.includes(needScrollToMessageId)
    ) {
      setNeedScrollToMessageId('');
      // The jump already started: its missing-target branch held isScrolling
      // (nothing releases it for a target that is never found) and aimed the
      // list at the oldest loaded message. Release it and go back to the
      // latest (index 0 of the inverted list).
      clearTimeout(scrollingResetTimerRef.current);
      setIsScrolling(false);
      _listRef.current?.scrollToIndex({ index: 0, animated: false });
      // TA-11079's exit-preview jump, if this was its target: its settle (which
      // ends the fallback and clears activeMessageId) will never run.
      clearExitFallback();
      if (exitedPreviewRef.current) {
        exitedPreviewRef.current = false;
        setActiveMessageId(undefined);
      }
    }
  }, [needScrollToMessageId, unavailableMessageIds]);

  // TA-11067: the pending re-aim of the jump in flight, keyed by its target.
  // List updates re-run the effect below; a re-run for the same target leaves
  // the pending re-aim alone instead of restarting its 2s window, and a new
  // target cancels it so it can't clear the new target's request.
  const settlingJumpRef = useRef<{
    id: string;
    timer: ReturnType<typeof setTimeout>;
  }>(undefined);
  useEffect(() => () => clearTimeout(settlingJumpRef.current?.timer), []);

  useEffect(() => {
    if (
      settlingJumpRef.current &&
      settlingJumpRef.current.id !== needScrollToMessageId
    ) {
      clearTimeout(settlingJumpRef.current.timer);
      settlingJumpRef.current = undefined;
      // TA-11079: the cancelled jump may have been the exit-preview one; its
      // settle (which clears activeMessageId) will never run, and the flag
      // must not leak into the new target's settle.
      exitedPreviewRef.current = false;
      clearExitFallback();
    }
    if (!needScrollToMessageId || _listRef?.current === null) return;
    const messageByCursor = filteredMessages?.filter(
      (filterMessage) => filterMessage.id === needScrollToMessageId
    );
    if (messageByCursor && messageByCursor.length > 0) {
      const targetId = messageByCursor[0].id;
      if (settlingJumpRef.current?.id === targetId) return;
      startJumpScrolling();
      scrollToMessageId(targetId);
      settlingJumpRef.current = {
        id: targetId,
        timer: setTimeout(() => {
          settlingJumpRef.current = undefined;
          // Second pass: the first jump lands by FlashList's estimated row
          // height, so re-aim once the rows it passed have been measured.
          scrollToMessageId(targetId);
          setNeedScrollToMessageId('');
          clearExitFallback();
          if (exitedPreviewRef.current) {
            // TA-11079: kept through the exit so the live list opened on the
            // message; clear it (and its highlight) now the jump has settled.
            exitedPreviewRef.current = false;
            setActiveMessageId(undefined);
          }
          scheduleScrollingReset();
        }, 2000),
      };
    } else {
      const fallbackId = exitFallbackRef.current?.id;
      const paging = fetchAfterCursor();
      if (fallbackId && fallbackId === needScrollToMessageId && paging) {
        // TA-11079: the fallback re-jump after an exit that couldn't reach
        // the message is bounded: a page that fails, brings nothing, or
        // can't exist ends it (the deadline in finishExitPreview covers
        // the rest), instead of leaving the spinner up for good.
        Promise.resolve(paging).then((status) => {
          if (
            status === 'failed' ||
            status === 'empty' ||
            (status === 'skipped' && !loading && !hasNextPage)
          ) {
            giveUpExitFallback(fallbackId);
          }
        });
      }
      startJumpScrolling();
      _listRef.current?.scrollToIndex({
        index: filteredMessages.length - 1,
        animated: true,
      });
    }
  }, [
    _listRef?.current,
    needScrollToMessageId,
    filteredMessages,
    displayMessageList?.length,
  ]);

  const groupCreatedInfo = () => {
    const owner = chat?.owner;

    return (
      <Box alignItems='center' marginBottom='s'>
        <ChatSearchText
          textToHighlight={` ${owner?.name ?? ''} has created a group “${
            chat?.name ?? ''
          }”`}
          searchWords={[owner?.name ?? '', chat?.name ?? '']}
        />

        {users
          ?.filter((user) => owner && user.id !== owner.id)
          .map((user) => (
            <ChatSearchText
              textToHighlight={` ${owner?.name ?? ''} has added ${
                user?.name ?? ''
              }`}
              key={user.id}
              searchWords={[owner?.name ?? '', user?.name ?? '']}
            />
          ))}
      </Box>
    );
  };

  // TA-11079: exit the preview onto the previewed message without a flash.
  // The live thread mounts at the newest message, so rather than switching at
  // once the preview stays on screen while the live list is paged back until
  // it holds the previewed message (EXIT_WALK_* caps). Only then is the
  // preview left, with activeMessageId still set so the live list opens on
  // the message; the jump's settle clears it. If a cap, a failure or the
  // timeout is hit first, it falls back to switching immediately and the
  // existing re-jump (the newest messages flash; no mask or overlay).
  const [isExitingPreview, setIsExitingPreview] = useState(false);
  const [exitWalkTick, setExitWalkTick] = useState(0);
  const exitTargetRef = useRef('');
  const exitPagesRef = useRef(0);
  const exitWalkingRef = useRef(false);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const exitChatIdRef = useRef(chatId);
  const exitedPreviewRef = useRef(false);
  exitChatIdRef.current = chatId;

  // The fallback re-jump (message not reached by the walk) ends at the
  // deadline, on a dead-end page, or when the jump settles/is replaced.
  const exitFallbackRef = useRef<{
    id: string;
    timer: ReturnType<typeof setTimeout>;
  }>(undefined);
  const clearExitFallback = () => {
    clearTimeout(exitFallbackRef.current?.timer);
    exitFallbackRef.current = undefined;
  };
  const giveUpExitFallback = (id: string) => {
    if (exitFallbackRef.current?.id !== id) return;
    clearExitFallback();
    // Only this fallback's request: a newer target keeps its own.
    setNeedScrollToMessageId((current) => (current === id ? '' : current));
    scheduleScrollingReset();
  };

  const finishExitPreview = (fellBack: boolean) => {
    const targetId = exitTargetRef.current;
    clearTimeout(exitTimerRef.current);
    exitTargetRef.current = '';
    exitWalkingRef.current = false;
    setIsExitingPreview(false);
    setIsSearchPreview(false);
    setSearch('');
    setIsSearching(false);
    setRepliedClicked('');
    setRepliedFromMessageId('');
    if (targetId) {
      exitedPreviewRef.current = !fellBack;
      setNeedScrollToMessageId(targetId);
      if (fellBack) {
        clearExitFallback();
        exitFallbackRef.current = {
          id: targetId,
          timer: setTimeout(
            () => giveUpExitFallback(targetId),
            EXIT_FALLBACK_TIMEOUT_MS
          ),
        };
      }
    }
    // Walked: keep activeMessageId so the live list opens on the message.
    if (fellBack || !targetId) setActiveMessageId(undefined);
  };

  const backToChat = () => {
    if (isExitingPreview) return;
    const targetId = messageIdNeedScrollTo;
    if (!targetId || !isInLiveList || !fetchLiveOlderPage) {
      exitTargetRef.current = targetId;
      finishExitPreview(true);
      return;
    }
    exitTargetRef.current = targetId;
    exitPagesRef.current = 0;
    exitWalkingRef.current = false;
    clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(
      () => isExitingPreviewRef.current && finishExitPreview(true),
      EXIT_WALK_TIMEOUT_MS
    );
    setIsExitingPreview(true);
  };
  const isExitingPreviewRef = useRef(false);
  isExitingPreviewRef.current = isExitingPreview;

  useEffect(() => {
    if (!isExitingPreview) return;
    const targetId = exitTargetRef.current;
    if (!targetId || !isInLiveList || !fetchLiveOlderPage) return;
    if (isInLiveList(targetId)) {
      finishExitPreview(false);
      return;
    }
    // Re-runs when the initial live query settles (liveLoading) or a page lands.
    if (liveLoading || exitWalkingRef.current) return;
    if (exitPagesRef.current >= EXIT_WALK_MAX_PAGES) {
      finishExitPreview(true);
      return;
    }
    exitWalkingRef.current = true;
    const walkChatId = chatId;
    fetchLiveOlderPage(EXIT_WALK_PAGE_SIZE).then((status) => {
      if (
        !isExitingPreviewRef.current ||
        exitChatIdRef.current !== walkChatId
      ) {
        return;
      }
      exitWalkingRef.current = false;
      // 'skipped' here means no older page (or none requested): nothing more
      // to walk, so stop like on failed/empty.
      if (status !== 'loaded') {
        finishExitPreview(true);
        return;
      }
      exitPagesRef.current += 1;
      setExitWalkTick((tick) => tick + 1);
    });
  }, [
    isExitingPreview,
    exitWalkTick,
    liveLoading,
    isInLiveList,
    fetchLiveOlderPage,
  ]);

  const abortExitWalk = () => {
    clearTimeout(exitTimerRef.current);
    exitTargetRef.current = '';
    exitWalkingRef.current = false;
    setIsExitingPreview(false);
  };

  // The walk belongs to the preview it started in. If something else leaves
  // the preview or re-aims it at another message (e.g. another search result
  // is clicked mid-walk), abandon the walk instead of finishing it for the
  // old target.
  useEffect(() => {
    if (!isExitingPreview) return;
    if (isSearchPreview && messageIdNeedScrollTo === exitTargetRef.current) {
      return;
    }
    abortExitWalk();
  }, [isExitingPreview, isSearchPreview, messageIdNeedScrollTo]);

  // Switching chats mid-walk abandons it; the preview flag belongs to the
  // chat it was opened for. A pending exit-jump flag goes with it.
  useEffect(
    () => () => {
      abortExitWalk();
      exitedPreviewRef.current = false;
      clearExitFallback();
    },
    [chatId]
  );
  if (isSearchPreview) {
    return (
      <Box flex={1}>
        <Box flex={1}>
          <ChatMessageList
            isPaginationResolved={isPaginationResolved}
            hasNextPage={isSearching ? canLoadMore : hasNextPage}
            firstNewMsgId={firstNewMsgId}
            onViewableMsgItemsChanged={onViewableMsgsChanged}
            list={chatMessageListData}
            filterVal={search}
            ref={_listRef}
            fetchAfterCursor={() => {
              if (isSearching && searchFrom.current > 0) {
                globalSearchCall(gatedTerm, isTagSelected, isFilesSelected);
              } else {
                fetchAfterCursor();
              }
            }}
            loading={isSearching ? isSearchResultLoading : loading}
            refreshing={refreshing}
            onRefresh={onRefresh}
            onLongPress={undefined}
            onPress={scrollToReplyMessage}
            highlightedToGrey01MessageId={repliedFromMessageId}
            messageIdNeedScrollTo={messageIdNeedScrollTo}
            isArchived={chat?.settings?.archivedAt ?? false}
          />
        </Box>
        <TouchableOpacity
          onPress={() => {
            backToChat();
          }}>
          <Box
            accessibilityLabel='previewMode'
            bg='grey05'
            width='100%'
            style={{ height: '36px' }}
            alignItems='center'
            justifyContent='center'
            flexDirection='row'>
            <Text
              fontSize={14}
              variant='metadataSecondary'
              fontWeight='500'
              color='grey01'>
              {isExitingPreview
                ? 'Loading chat…'
                : 'Preview mode – click here to start chatting.'}
            </Text>
            {isExitingPreview && (
              <ActivityIndicator
                size='small'
                color={theme.colors.grey01}
                style={{ marginLeft: theme.spacing.xs }}
              />
            )}
          </Box>
        </TouchableOpacity>
      </Box>
    );
  }

  const getChatForm = () => {
    let chatFormFirstActivity: (() => void) | undefined;
    if (isActive) {
      chatFormFirstActivity = handleChatFirstActivity;
    } else if (showHashTagStep3Tooltip) {
      chatFormFirstActivity = handleHashTagStep3Activity;
    }

    return (
      <TooltipOnboardingWeb
        variant='left-center'
        tooltipStyle='success'
        title={t('models:chat.onboarding.assignTooltip.title')}
        description={t('models:chat.onboarding.assignTooltip.description')}
        open={isActive && isOpenChat}
        forceShow={isActive && isOpenChat}
        trigger='press'
        style={{ alignSelf: 'stretch' }}
        offset={5}
        triggerAnchorHeight={99}
        animatedOpacity={tooltipOpacity}
        onOpenChange={(open) => {
          if (!open && isActive) setAssignTooltipDismissed(true);
        }}>
        <TooltipOnboardingWeb
          variant='left-center'
          tooltipStyle='success'
          title={t('models:chat.onboarding.hashTagStep3.title')}
          description={t('models:chat.onboarding.hashTagStep3.description')}
          forceShow={
            onboardingActive &&
            showHashTagStep3Tooltip &&
            !isInUploadOnboardingFlow
          }
          disabled={
            !onboardingActive ||
            !showHashTagStep3Tooltip ||
            isInUploadOnboardingFlow
          }
          trigger='press'
          offset={5}
          triggerAnchorHeight={99}
          animatedOpacity={step3TooltipOpacity}
          style={{ alignSelf: 'stretch' }}>
          <Box>
            <ChatForm
              autoFocusInput={focus}
              onSend={sendMessage}
              isGroupChat={isGroupChatResolved}
              chatUsers={users || []}
              chatId={derivedChatId}
              isBlocked={chat?.isUserBlocked}
              isPendingRequest={chat?.isPendingRequest ?? false}
              showLeaveMessage={shouldShowNoLongerMessage}
              disableInput={
                !!chat?.settings?.archivedAt || shouldShowNoLongerMessage
              }
              onFirstActivity={chatFormFirstActivity}
              highlightTagButton={onboardingActive && showNudgeCard}
              onHighlightTagPress={handleHighlightTagPress}
              showTagOnboardingTooltip={
                onboardingActive && showUploadTagTooltip
              }
              onTagOnboardingTryIt={handleUploadTooltipTryIt}
              showStep3Tooltip={
                onboardingActive &&
                showHashTagStep3Tooltip &&
                isInUploadOnboardingFlow
              }
              step3TooltipAnimatedOpacity={step3TooltipOpacity}
              onStep3TooltipActivity={handleHashTagStep3Activity}
            />
          </Box>
        </TooltipOnboardingWeb>
      </TooltipOnboardingWeb>
    );
  };

  const getPendingInviteCard = () => (
    <Box paddingHorizontal='m' paddingTop='m' paddingBottom='m'>
      <Box alignItems='center' marginBottom='s' flexDirection='row'>
        <Box borderBottomColor='grey02' borderBottomWidth={1} flex={1} />
        <Text variant='metadata' mx='m' color='grey03'>
          {currentUserPendingInvite.length > 0
            ? t('format:timeAgoMessageList', {
                val: currentUserPendingInvite[0].createdAt,
              })
            : ''}
        </Text>
        <Box borderBottomColor='grey02' borderBottomWidth={1} flex={1} />
      </Box>
      <Box flexDirection='row' alignItems='flex-start'>
        <Avatar
          id={chat?.id}
          avatarType='chat'
          size='small'
          disableAvatarPress={true}
          backgroundColor={theme.colors.grey02}
          avatar={chat?.avatar}
          avatarUrl={chat?.avatarUrl}
          isMultiple={true}
          label={chat?.name ?? getChatAvatarLabel(chat)}
        />
        <Box flex={1} marginLeft='s'>
          <Box flexDirection='row'>
            <Text variant='webSmall' color='textPrimary'>
              {chat?.name ?? ''}
            </Text>

            <Text ml='xxs' variant='metadataSecondary' color='grey04'>
              {currentUserPendingInvite.length > 0
                ? t('format:time', {
                    val: currentUserPendingInvite[0].createdAt,
                  })
                : ''}
            </Text>
          </Box>
          <Text marginTop='xs' variant='webBodySecondary' color='textSecondary'>
            {`You’ve been invited by ${
              chat?.owner?.name ?? ''
            } to join this group, this invitation will expire in 7 days.`}
          </Text>
          <Box
            mt='xs'
            flexDirection='row'
            alignItems='center'
            marginHorizontal='xs'
            paddingBottom='s'>
            <AcceptDeclineButtons
              borderRadius='xs'
              acceptIconName='CheckCircle'
              fromChatDetail={true}
              suffix={true}
              requestId={currentUserPendingInvite[0].id}
              toggleAcceptDecline={() => {
                setIsOpenChat(false);
                setActiveChat(undefined);
                setShouldChatListOpen(true);
                setChatListOpen(true);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const chatMessageList = () => {
    return (
      <ChatMessageList
        isPaginationResolved={isPaginationResolved}
        hasNextPage={isSearching ? canLoadMore : hasNextPage}
        firstNewMsgId={firstNewMsgId}
        showNewMessageIndicator={(newMsgId) => {
          if (!isSearching) {
            if (firstNewMsgId === '') {
              setFirstNewMsgId(newMsgId);
            }
            setShowNewMessageIndicator(true);
          } else {
            setShowNewMessageIndicator(false);
          }
        }}
        onViewableMsgItemsChanged={onViewableMsgsChanged}
        list={chatMessageListData}
        filterVal={search}
        ref={_listRef}
        fetchAfterCursor={() => {
          if (isSearching && searchFrom.current > 0) {
            globalSearchCall(gatedTerm, isTagSelected, isFilesSelected);
          } else {
            fetchAfterCursor();
          }
        }}
        loading={isSearching ? isSearchResultLoading : loading}
        refreshing={refreshing}
        onRefresh={onRefresh}
        onLongPress={undefined}
        onPress={scrollToReplyMessage}
        onEditTagPress={() => {
          setIsEditTagModalOpen(true);
        }}
        onForwardPress={() => {
          setIsForwardMessageModalOpen(true);
        }}
        onDeletedPress={() => {
          setIsDeleteMessageModalOpen(true);
        }}
        onReadByPress={() => {
          setIsReadByModalOpen(true);
        }}
        highlightedToGrey01MessageId={repliedFromMessageId}
        messageIdNeedScrollTo={messageIdNeedScrollTo}
        // TA-11079: after a walked exit the live list opens at FlashList's
        // estimated position; keep the previewed message centred as rows
        // measure, until the jump settles or the user scrolls.
        onJumpContentSizeChange={
          exitedPreviewRef.current && needScrollToMessageId
            ? () => scrollToMessageId(needScrollToMessageId)
            : undefined
        }
        isArchived={chat?.settings?.archivedAt ?? false}
        coachmarkEnabled={
          onboardingActive &&
          Platform.OS === 'web' &&
          !!me &&
          !me?.onboardingState?.tagPillCoachmarkShown
        }
        onCoachmarkSeen={handleCoachmarkSeen}
      />
    );
  };

  const getChatMessageListBox = () =>
    isVisibleForChat && showOverlayForChat ? (
      <Box
        position='absolute'
        bottom={
          chatTutorialWebState === (isFromChatList ? '7' : '6') ||
          chatTutorialWebState === '1'
            ? chatFormHeight
            : 0
        }
        style={{
          width: '100%',
        }}>
        <Box
          style={{
            width: '100%',
            maxHeight: maxHeight,
          }}>
          {chatMessageList()}
        </Box>
      </Box>
    ) : (
      chatMessageList()
    );

  const myTagModal = (needMarginLeft: boolean) => {
    const isInTutorialOverlay = isVisibleForChat && showOverlayForChat;
    return (
      (isTagModalOpen || isCompletingTask || isAssigningTask) && (
        <Box
          position={isInTutorialOverlay ? undefined : 'absolute'}
          bottom={isInTutorialOverlay ? undefined : 90}
          width={440}
          height='60vh'
          padding={undefined}
          backgroundColor={undefined}
          borderRadius={undefined}
          zIndex={3}
          ml={needMarginLeft ? 'm' : undefined}>
          <TooltipOnboardingWeb
            variant='left-center'
            tooltipStyle='success'
            title={t('models:chat.onboarding.hashTagStep2.title')}
            description={t('models:chat.onboarding.hashTagStep2.description')}
            forceShow={showHashTagStep2Tooltip && !isInUploadOnboardingFlow}
            disabled={!showHashTagStep2Tooltip || isInUploadOnboardingFlow}
            trigger='press'
            offset={16}
            triggerAnchorHeight={260}
            style={{ flex: 1, alignSelf: 'stretch' }}>
            <Box flex={1}>
              <MyTagModal
                chatId={chatId}
                isFromChatAssignTask={isAssigningTask === true}
                isFromChatCompleteTask={isCompletingTask === true}
                filterVal=''
                showUploadOnboardingTooltip={isInUploadOnboardingFlow}
                onSelect={() => {
                  if (
                    ((firstChatSentState ||
                      onboardingMutationFiredRef.current) &&
                      !firstTagUsedState) ||
                    isInUploadOnboardingFlow
                  ) {
                    setShowHashTagStep3Tooltip(true);
                  }
                  if (!focus) setFocus(true);
                }}
                onCancel={() => {
                  setIsInUploadOnboardingFlow(false);
                }}
              />
            </Box>
          </TooltipOnboardingWeb>
        </Box>
      )
    );
  };

  const chatAssignToTaskModal = (needMarginLeft: boolean) => {
    return (
      isAssigningTaskId &&
      isAssigningTaskStep2 && (
        <Box
          position='absolute'
          bottom={90}
          width={440}
          height='60vh'
          zIndex={3}
          ml={needMarginLeft ? 'm' : undefined}>
          <ChatAssignToTaskModal
            chatId={activeChatId}
            assignedTaskId={isAssigningTaskId}
            onSave={() => {
              if (me?.onboardingState?.firstChatSent === false) {
                setTimeout(() => {
                  setAssignTooltipChecked(true);
                  setAssignTooltipDismissed(false);
                  setShowOnboardingChatFromViewTask(true);
                  show();

                  updateOnboardingState({
                    variables: { attributes: { firstChatSent: true } },
                    refetchQueries: [{ query: GetMeDocument }],
                  });
                }, 2000);
              }
            }}
          />
        </Box>
      )
    );
  };

  const getComponent = () => {
    let content = null;

    if (shouldShowPendingInviteCard) {
      content = <Box>{getPendingInviteCard()}</Box>;
    } else if (shouldRenderInputContainer) {
      content = (
        <Box
          onLayout={(e) => {
            setChatFormHeight(e.nativeEvent.layout.height);
          }}>
          {getChatForm()}
        </Box>
      );
    }

    return content;
  };
  return (
    <>
      <DropZone onDrop={onDrop}>
        {({ Input: DropZoneInput, isDragActive }) => (
          <Box
            ref={tagRef}
            flex={1}
            style={{
              paddingBottom: insets.bottom,
            }}>
            <Box
              style={{ display: isDragActive ? 'flex' : 'none' }}
              position='absolute'
              zIndex={1}
              top={0}
              bottom={0}
              right={0}
              left={0}
              alignContent='center'
              justifyContent='center'>
              <Box
                backgroundColor='greenBrand'
                position='absolute'
                opacity={0.9}
                top={0}
                bottom={0}
                right={0}
                left={0}
              />
              <Box flex={1} justifyContent='center'>
                <Text
                  variant='labelEmphasized'
                  color='black'
                  style={{
                    textAlign: 'center',
                  }}>
                  Drop files here to attach them to your message.
                </Text>
                <DropZoneInput />
              </Box>
            </Box>

            <Box flex={1}>
              {!showThreadSkeleton &&
                messages.length == 0 &&
                isGroupChat &&
                chat?.users?.some((t) => t.id === me?.id) && (
                  <Box
                    mb='s'
                    flex={1}
                    justifyContent='flex-end'
                    alignItems='center'
                    alignContent='center'
                    flexDirection='column'>
                    {groupCreatedInfo()}
                  </Box>
                )}
              {!showThreadSkeleton &&
                messages.length == 0 &&
                isGroupChat === false && (
                  <Box
                    flex={1}
                    justifyContent='center'
                    alignItems='center'
                    alignContent='center'
                    flexDirection='column'>
                    <Icon name='BubbleAngle' width={120} height={93} />
                    <Text
                      variant='labelEmphasized'
                      textAlign='center'
                      paddingBottom='xs'
                      mt='m'
                      color='textPrimary'>
                      {t('models:chat.emptyState.noMessage')}
                    </Text>
                  </Box>
                )}

              {/* TA-10279: previously collapsed to zero height whenever
                  `messages` was empty — squashing the first-open bubble
                  skeleton (rendered inside `ChatMessageList` below) to
                  nothing during the exact window it needs to be visible. */}
              <Box flex={messages.length > 0 || showThreadSkeleton ? 1 : 0}>
                {isVisibleForChat &&
                showOverlayForChat &&
                (chatTutorialWebState === '2' ||
                  chatTutorialWebState === (isFromChatList ? '8' : '7')) &&
                !loading ? (
                  <>
                    <ChatOverlay
                      visible={
                        isVisibleForChat &&
                        showOverlayForChat &&
                        (chatTutorialWebState === '2' ||
                          chatTutorialWebState === (isFromChatList ? '8' : '7'))
                      }
                      children={
                        <Box
                          onLayout={(e) => {
                            setChatMessageListHeight(
                              e.nativeEvent.layout.height <= maxHeight
                                ? e.nativeEvent.layout.height
                                : maxHeight
                            );
                          }}
                          position='absolute'
                          bottom={
                            chatFormHeight > 0
                              ? chatFormHeight -
                                (chatMessageListHeight < maxHeight
                                  ? theme.spacing.m + theme.spacing.xs
                                  : 0)
                              : undefined
                          }
                          borderTopLeftRadius='xs'
                          borderTopRightRadius='xs'
                          style={{
                            width: '100%',
                            maxHeight: maxHeight,
                            backgroundColor:
                              chatMessageListHeight > 0
                                ? theme.colors.grey01
                                : undefined,
                          }}>
                          {chatMessageList()}
                        </Box>
                      }
                    />
                  </>
                ) : (
                  getChatMessageListBox()
                )}
                <NewMessageIndicator
                  show={showNewMessageIndicator}
                  onPress={scrollToFirstNewMsg}
                />
              </Box>
            </Box>

            {isVisibleForChat &&
              showOverlayForChat &&
              chatTutorialWebState === '1' && (
                <ChatOverlay
                  visible={
                    isTagModalOpen || isCompletingTask || isAssigningTask
                  }
                  children={myTagModal(false)}
                />
              )}

            {(!isVisibleForChat ||
              !showOverlayForChat ||
              (isVisibleForChat &&
                chatTutorialWebState !== '1' &&
                chatTutorialWebState !== (isFromChatList ? '6' : '5'))) &&
              myTagModal(
                !isVisibleForChat ||
                  !showOverlayForChat ||
                  (isVisibleForChat &&
                    chatTutorialWebState !== '1' &&
                    chatTutorialWebState !== (isFromChatList ? '6' : '5'))
              )}

            {isVisibleForChat &&
              showOverlayForChat &&
              chatTutorialWebState === '1' && (
                <ChatOverlay
                  visible={!!isAssigningTaskId && isAssigningTaskStep2}
                  children={chatAssignToTaskModal(false)}
                />
              )}

            {(!isVisibleForChat ||
              !showOverlayForChat ||
              (isVisibleForChat && chatTutorialWebState !== '1')) &&
              chatAssignToTaskModal(
                !isVisibleForChat ||
                  !showOverlayForChat ||
                  (isVisibleForChat && chatTutorialWebState !== '1')
              )}

            {isEditTagModalOpen && (
              <Box
                position='absolute'
                bottom={90}
                width={440}
                height='60vh'
                zIndex={3}
                ml='l'>
                <EditMyTagsModal
                  onDonePress={() => {
                    if (noNeedRefetchRef) noNeedRefetchRef.current = true;
                    setIsEditTagModalOpen(false);
                  }}
                  onCancelPress={() => {
                    setIsEditTagModalOpen(false);
                  }}
                />
              </Box>
            )}
            {isReadByModalOpen && (
              <Box
                position='absolute'
                bottom={90}
                width={440}
                height='60vh'
                zIndex={3}
                alignSelf='center'>
                <ChatMessageReadByModal
                  onBack={() => setIsReadByModalOpen(false)}
                />
              </Box>
            )}
            {isForwardMessageModalOpen && (
              <Box
                position='absolute'
                bottom={90}
                width={440}
                height='60vh'
                zIndex={3}
                ml='l'>
                <ForwardMessage
                  messageData={selectedMessage}
                  onForwardCompleted={() => {
                    setIsForwardMessageModalOpen(false);
                  }}
                  onCancelPress={() => {
                    setIsForwardMessageModalOpen(false);
                  }}
                />
              </Box>
            )}
            {showViewTaskSuccessMsg && !isGroupChatResolved && (
              <Box
                mb='xs'
                alignItems='flex-start'
                marginLeft='xxl'
                paddingLeft='s'>
                <Text variant='webSmall' color='black'>
                  {t('models:chat.onboarding.viewTaskMsgSent', {
                    name: invitedMember?.name?.split(' ')[0] ?? '',
                  })}
                </Text>
              </Box>
            )}
            {!(showViewTaskSuccessMsg && !isGroupChatResolved) &&
              firstMsgJustSent && (
                <Box
                  mb='xs'
                  alignItems='flex-start'
                  marginLeft='xxl'
                  paddingLeft='s'
                  style={{ marginTop: -theme.spacing.m }}>
                  <Text variant='webSmall' color='black'>
                    {t('models:chat.onboarding.firstMessageSent')}
                  </Text>
                </Box>
              )}
            {onboardingActive && firstTagJustUsed && (
              <Box
                mb='xs'
                alignItems='flex-start'
                marginLeft='xxl'
                paddingLeft='s'>
                <Text variant='webSmall' color='black'>
                  {firstTagProjectName
                    ? t('models:chat.onboarding.firstTagSavedTo', {
                        projectName: firstTagProjectName,
                      })
                    : t('models:chat.onboarding.firstTagComplete')}
                </Text>
              </Box>
            )}
            {onboardingActive && showNudgeCard && (
              <ChatOnboardingNudge
                title={t('models:chat.onboarding.nudge.title')}
                description={t('models:chat.onboarding.nudge.description', {
                  name: invitedMember?.name ?? '',
                })}
              />
            )}
            {onboardingActive && showPhotoRequestNudge && (
              <ChatPhotoRequestNudge
                title={t('models:chat.onboarding.photoNudge.title')}
                description={t('models:chat.onboarding.photoNudge.description')}
                ctaLabel={t('models:chat.onboarding.photoNudge.cta')}
                onCta={() => {
                  setShowPhotoRequestNudge(false);
                  setMessage(t('models:chat.onboarding.photoNudge.message'));
                  setShowNudgeCard(true);
                  setFocus(true);
                }}
              />
            )}
            {getComponent()}
            {shouldRenderWebNoLongerMessageBar && (
              <Box
                onLayout={(e) => {
                  setChatFormHeight(e.nativeEvent.layout.height);
                }}>
                <Box
                  borderTopWidth={1}
                  borderColor='grey02'
                  backgroundColor='grey02'
                  paddingVertical='m'
                  paddingHorizontal='m'
                  alignItems='center'
                  justifyContent='center'>
                  <Text
                    variant='metadataSecondary'
                    color='onSurfaceSecondary'
                    fontWeight='500'
                    textAlign='center'>
                    {chat?.isPendingRequest
                      ? t('shared:noChatMemberMessage')
                      : t('shared:noLongerMessage')}
                  </Text>
                </Box>
              </Box>
            )}
            {isDeleteMessageModalOpen && (
              <ConfirmModal
                showModal={isDeleteMessageModalOpen}
                onClose={() => setIsDeleteMessageModalOpen(false)}
                onPress={() =>
                  selectedMessage &&
                  deleteMessageById(selectedMessage, onCompleted, onErrorDelete)
                }
                buttonText={t('shared:delete')}
                title={t('models:chat.delete.message.cta')}
                message={t('models:chat.delete.message.message')}
              />
            )}
            {isNetInfoModalVisible && (
              <NetInfoModal
                onClose={() => setIsNetInfoModalVisible(false)}
                showModal={isNetInfoModalVisible}
                title={t('models:chat.netInfoTitle')}
                message={t('models:chat.notConnectedToInternet')}
                buttonTextOne={t('models:onBoarding.error.okPress')}
              />
            )}
            {repliedClicked ||
            (needScrollToMessageId && !exitedPreviewRef.current) ? (
              <ActivityIndicatorLoading />
            ) : null}
          </Box>
        )}
      </DropZone>
    </>
  );
};

export default React.memo(ChatDetail);
